/**
 * Record / Edit an In-Kind (Physical Item) Donation.
 *
 * Emits noncash-receipt data — dollar values are ADMIN ONLY (internal
 * estimated value) and never printed on the donor receipt.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { X, Plus, Trash2, Save, Send, AlertCircle, Loader2, Upload, Image as ImageIcon } from 'lucide-react';
import { isoLocalDate, tzAbbrev } from '../../utils/formatDate';
import MediaPicker, { mediaServeUrl } from './MediaPicker';
import { processAndUpload, ACCEPTED_ACCEPT_ATTR } from '../../lib/mediaUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CONDITIONS = [
  { v: 'new', label: 'New' },
  { v: 'like_new', label: 'Like New' },
  { v: 'good', label: 'Good' },
  { v: 'fair', label: 'Fair' },
];

const emptyItem = () => ({
  category: '',
  description: '',
  quantity: 1,
  condition: 'good',
  inventory_category: '',
  sku: '',
});

export default function RecordInKindDonationModal({
  open, initial, onClose, onSaved,
}) {
  const [form, setForm] = useState({
    donor_name: '',
    donor_email: '',
    donor_phone: '',
    received_at: isoLocalDate(new Date()),
    received_by_staff: '',
    location_received: '',
    internal_notes: '',
    internal_estimated_value: '',
    items: [emptyItem()],
    photo_ids: [],
  });
  const [categories, setCategories] = useState([]);
  const [donorHint, setDonorHint] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const fileInputRef = React.useRef(null);
  const editing = Boolean(initial?.id);

  const MAX_PHOTOS = 8;

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        donor_name: initial.donor_name || '',
        donor_email: initial.donor_email || '',
        donor_phone: initial.donor_phone || '',
        received_at: initial.received_at || isoLocalDate(new Date()),
        received_by_staff: initial.received_by_staff || '',
        location_received: initial.location_received || '',
        internal_notes: initial.internal_notes || '',
        internal_estimated_value: initial.internal_estimated_value ?? '',
        items: (initial.items || []).map((it) => ({
          category: it.category || '',
          description: it.description || '',
          quantity: it.quantity || 1,
          condition: it.condition || 'good',
          inventory_category: it.inventory_category || '',
          sku: it.sku || '',
        })),
        photo_ids: initial.photo_ids || [],
      });
    } else {
      setForm((f) => ({ ...f, items: [emptyItem()] }));
    }
    setDonorHint(null);
    setError('');
    axios.get(`${BACKEND_URL}/api/admin/in-kind-donations/categories`, { withCredentials: true })
      .then((r) => setCategories(r.data?.categories || []))
      .catch(() => setCategories([]));
  }, [open, initial]);

  const lookupDonor = useCallback(async (email, phone) => {
    if (!email && !phone) { setDonorHint(null); return; }
    try {
      const r = await axios.get(
        `${BACKEND_URL}/api/admin/in-kind-donations/donor-lookup`,
        { params: { email, phone }, withCredentials: true },
      );
      const em = r.data?.email_match;
      const pm = r.data?.phone_matches || [];
      if (em) {
        setDonorHint({ type: 'email', match: em });
      } else if (pm.length > 0) {
        setDonorHint({ type: 'phone', matches: pm });
      } else {
        setDonorHint(null);
      }
    } catch {
      setDonorHint(null);
    }
  }, []);

  const applyHint = (match) => {
    setForm((f) => ({
      ...f,
      donor_name: match.donor_name || f.donor_name,
      donor_email: match.donor_email || f.donor_email,
      donor_phone: match.donor_phone || f.donor_phone,
    }));
    setDonorHint(null);
  };

  const updateItem = (i, patch) => {
    setForm((f) => {
      const items = [...f.items];
      items[i] = { ...items[i], ...patch };
      return { ...f, items };
    });
  };

  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, emptyItem()] }));
  const removeItem = (i) => setForm((f) => ({
    ...f, items: f.items.length > 1 ? f.items.filter((_, idx) => idx !== i) : f.items,
  }));

  const addPhotoIds = (ids) => setForm((f) => {
    const merged = [...(f.photo_ids || []), ...ids];
    // Dedupe while preserving order; cap at MAX_PHOTOS.
    const seen = new Set();
    const unique = merged.filter((x) => (seen.has(x) ? false : (seen.add(x), true)));
    return { ...f, photo_ids: unique.slice(0, MAX_PHOTOS) };
  });
  const removePhotoId = (id) => setForm((f) => ({
    ...f, photo_ids: (f.photo_ids || []).filter((x) => x !== id),
  }));

  const onDeviceUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    setUploadPct(0);
    setError('');
    try {
      const room = MAX_PHOTOS - (form.photo_ids || []).length;
      if (room <= 0) {
        setError(`Maximum ${MAX_PHOTOS} photos per donation.`);
        return;
      }
      const trimmed = files.slice(0, room);
      const uploaded = await processAndUpload(trimmed, (pct) => setUploadPct(pct));
      addPhotoIds(uploaded.map((u) => u.id));
      if (uploaded.partialErrors?.length) {
        setError(`Some uploads failed: ${uploaded.partialErrors[0]}`);
      }
    } catch (err) {
      setError(err.message || 'Upload failed.');
    } finally {
      setUploading(false);
      setUploadPct(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const submit = async ({ andSend }) => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        internal_estimated_value: form.internal_estimated_value === ''
          ? null
          : Number(form.internal_estimated_value),
        items: form.items.map((it) => ({
          category: (it.category || '').trim(),
          description: (it.description || '').trim(),
          quantity: Number(it.quantity) || 0,
          condition: it.condition,
          inventory_category: (it.inventory_category || '').trim() || null,
          sku: (it.sku || '').trim() || null,
        })),
      };
      let saved;
      if (editing) {
        const r = await axios.patch(
          `${BACKEND_URL}/api/admin/in-kind-donations/${initial.id}`,
          payload, { withCredentials: true },
        );
        saved = r.data;
        if (andSend) {
          await axios.post(
            `${BACKEND_URL}/api/admin/in-kind-donations/${initial.id}/send-receipt`,
            {}, { withCredentials: true },
          );
        }
      } else {
        payload.send_receipt_now = Boolean(andSend);
        const r = await axios.post(
          `${BACKEND_URL}/api/admin/in-kind-donations`,
          payload, { withCredentials: true },
        );
        saved = r.data?.donation;
      }
      onSaved?.(saved);
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9500] flex items-start justify-center bg-black/60 p-4 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="in-kind-modal"
    >
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full my-8">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white rounded-t-xl">
          <div>
            <h2 className="text-lg font-semibold text-[#0A0A0A]">
              {editing ? 'Edit In-Kind Donation' : 'Record In-Kind Donation'}
            </h2>
            <p className="text-xs text-[#4B5563] mt-0.5">
              Physical item contribution. Dollar values are internal only —
              never printed on the donor receipt.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1"
            data-testid="in-kind-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Donor Info */}
          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-[#0A0A0A] uppercase tracking-wide">Donor</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#4B5563] font-medium">Full Name *</label>
                <input
                  type="text" required
                  value={form.donor_name}
                  onChange={(e) => setForm({ ...form, donor_name: e.target.value })}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  data-testid="in-kind-donor-name"
                />
              </div>
              <div>
                <label className="text-xs text-[#4B5563] font-medium">Email</label>
                <input
                  type="email"
                  value={form.donor_email}
                  onChange={(e) => setForm({ ...form, donor_email: e.target.value })}
                  onBlur={() => lookupDonor(form.donor_email, '')}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  data-testid="in-kind-donor-email"
                />
              </div>
              <div>
                <label className="text-xs text-[#4B5563] font-medium">Phone</label>
                <input
                  type="tel"
                  value={form.donor_phone}
                  onChange={(e) => setForm({ ...form, donor_phone: e.target.value })}
                  onBlur={() => lookupDonor('', form.donor_phone)}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  data-testid="in-kind-donor-phone"
                />
              </div>
              <div>
                <label className="text-xs text-[#4B5563] font-medium">Date Received ({tzAbbrev()}) *</label>
                <input
                  type="date" required
                  value={form.received_at}
                  onChange={(e) => setForm({ ...form, received_at: e.target.value })}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  data-testid="in-kind-received-at"
                />
              </div>
              <div>
                <label className="text-xs text-[#4B5563] font-medium">Received By (Staff)</label>
                <input
                  type="text"
                  value={form.received_by_staff}
                  onChange={(e) => setForm({ ...form, received_by_staff: e.target.value })}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  data-testid="in-kind-received-by"
                />
              </div>
              <div>
                <label className="text-xs text-[#4B5563] font-medium">Location Received</label>
                <input
                  type="text"
                  value={form.location_received}
                  onChange={(e) => setForm({ ...form, location_received: e.target.value })}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  data-testid="in-kind-location"
                />
              </div>
            </div>

            {donorHint?.type === 'email' && donorHint.match && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start justify-between gap-3" data-testid="in-kind-donor-hint-email">
                <div className="text-sm">
                  <div className="font-semibold text-amber-900">Possible existing donor found</div>
                  <div className="text-amber-800 mt-0.5">
                    {donorHint.match.donor_name} · {donorHint.match.donor_email} ({donorHint.match.source})
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => applyHint(donorHint.match)}
                    className="px-3 py-1.5 bg-amber-600 text-white rounded text-xs font-medium hover:bg-amber-700"
                    data-testid="in-kind-donor-hint-use"
                  >Use Existing</button>
                  <button
                    onClick={() => setDonorHint(null)}
                    className="px-3 py-1.5 bg-white border border-amber-300 text-amber-800 rounded text-xs font-medium hover:bg-amber-50"
                    data-testid="in-kind-donor-hint-new"
                  >Create New</button>
                </div>
              </div>
            )}

            {donorHint?.type === 'phone' && (donorHint.matches?.length || 0) > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3" data-testid="in-kind-donor-hint-phone">
                <div className="font-semibold text-blue-900 text-sm mb-2">
                  Possible existing donor(s) matched by phone
                </div>
                <div className="space-y-2">
                  {donorHint.matches.map((m, i) => (
                    <div key={i} className="flex items-center justify-between gap-3 bg-white rounded px-3 py-2 text-sm">
                      <div className="text-blue-900">
                        {m.donor_name || '—'} · {m.donor_email || 'no email'} · {m.donor_phone}
                      </div>
                      <button
                        onClick={() => applyHint(m)}
                        className="px-3 py-1 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700"
                        data-testid={`in-kind-donor-hint-phone-use-${i}`}
                      >Use</button>
                    </div>
                  ))}
                  <button
                    onClick={() => setDonorHint(null)}
                    className="text-xs text-blue-700 hover:underline"
                    data-testid="in-kind-donor-hint-phone-dismiss"
                  >Ignore — create a new donor</button>
                </div>
              </div>
            )}
          </section>

          {/* Items */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-[#0A0A0A] uppercase tracking-wide">Items</h3>
              <button
                onClick={addItem}
                className="text-xs text-[#BA1826] font-semibold hover:underline flex items-center gap-1"
                data-testid="in-kind-add-item"
              >
                <Plus className="w-3.5 h-3.5" />Add another item
              </button>
            </div>
            <div className="space-y-3">
              {form.items.map((it, i) => (
                <div key={i} className="border border-gray-200 rounded-lg p-3 space-y-2" data-testid={`in-kind-item-${i}`}>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                    <div className="md:col-span-2">
                      <label className="text-xs text-[#4B5563] font-medium">Category *</label>
                      <input
                        type="text" list={`cats-${i}`} required
                        value={it.category}
                        onChange={(e) => updateItem(i, { category: e.target.value })}
                        className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                        data-testid={`in-kind-item-${i}-category`}
                      />
                      <datalist id={`cats-${i}`}>
                        {categories.map((c) => <option key={c} value={c} />)}
                      </datalist>
                    </div>
                    <div>
                      <label className="text-xs text-[#4B5563] font-medium">Quantity *</label>
                      <input
                        type="number" min="1" required
                        value={it.quantity}
                        onChange={(e) => updateItem(i, { quantity: e.target.value })}
                        className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                        data-testid={`in-kind-item-${i}-qty`}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#4B5563] font-medium">Condition *</label>
                      <select
                        value={it.condition}
                        onChange={(e) => updateItem(i, { condition: e.target.value })}
                        className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                        data-testid={`in-kind-item-${i}-condition`}
                      >
                        {CONDITIONS.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-[#4B5563] font-medium">Description</label>
                    <input
                      type="text"
                      value={it.description}
                      onChange={(e) => updateItem(i, { description: e.target.value })}
                      placeholder='e.g., "Adult wheelchair, folding"'
                      className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                      data-testid={`in-kind-item-${i}-desc`}
                    />
                  </div>
                  <details className="text-xs text-[#4B5563]">
                    <summary className="cursor-pointer select-none hover:text-[#0A0A0A]">Optional: inventory tag / SKU</summary>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <input
                        type="text" placeholder="Inventory category"
                        value={it.inventory_category}
                        onChange={(e) => updateItem(i, { inventory_category: e.target.value })}
                        className="border border-gray-300 rounded px-2 py-1.5 text-sm"
                        data-testid={`in-kind-item-${i}-invcat`}
                      />
                      <input
                        type="text" placeholder="SKU (optional)"
                        value={it.sku}
                        onChange={(e) => updateItem(i, { sku: e.target.value })}
                        className="border border-gray-300 rounded px-2 py-1.5 text-sm"
                        data-testid={`in-kind-item-${i}-sku`}
                      />
                    </div>
                  </details>
                  {form.items.length > 1 && (
                    <button
                      onClick={() => removeItem(i)}
                      className="text-red-600 text-xs hover:underline flex items-center gap-1"
                      data-testid={`in-kind-item-${i}-remove`}
                    >
                      <Trash2 className="w-3 h-3" />Remove item
                    </button>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Photos (optional) */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-[#0A0A0A] uppercase tracking-wide">
                Photos <span className="text-[10px] text-[#4B5563] normal-case font-normal">(optional · up to {MAX_PHOTOS})</span>
              </h3>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading || (form.photo_ids?.length || 0) >= MAX_PHOTOS}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 rounded text-xs font-medium hover:bg-gray-50 disabled:opacity-40"
                  data-testid="in-kind-photo-upload-btn"
                >
                  {uploading
                    ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Uploading {uploadPct}%</>
                    : <><Upload className="w-3.5 h-3.5" />Upload from Device</>}
                </button>
                <button
                  type="button"
                  onClick={() => setPickerOpen(true)}
                  disabled={(form.photo_ids?.length || 0) >= MAX_PHOTOS}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 rounded text-xs font-medium hover:bg-gray-50 disabled:opacity-40"
                  data-testid="in-kind-photo-pick-btn"
                >
                  <ImageIcon className="w-3.5 h-3.5" />Pick from Library
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPTED_ACCEPT_ATTR}
                  multiple
                  className="hidden"
                  onChange={onDeviceUpload}
                  data-testid="in-kind-photo-file-input"
                />
              </div>
            </div>

            {(form.photo_ids?.length || 0) === 0 ? (
              <div
                className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center text-xs text-[#9CA3AF]"
                data-testid="in-kind-photo-empty"
              >
                No photos attached yet. Upload from device or pick from the Media Library.
              </div>
            ) : (
              <div
                className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2"
                data-testid="in-kind-photo-grid"
              >
                {(form.photo_ids || []).map((id) => (
                  <div
                    key={id}
                    className="relative aspect-square rounded overflow-hidden border border-gray-200 group"
                    data-testid={`in-kind-photo-tile-${id}`}
                  >
                    <img
                      src={mediaServeUrl(id, 'thumb')}
                      alt="Donation"
                      className="w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.style.opacity = '0.4'; }}
                    />
                    <button
                      type="button"
                      onClick={() => removePhotoId(id)}
                      className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                      title="Remove photo"
                      data-testid={`in-kind-photo-remove-${id}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Internal (admin-only) */}
          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-[#0A0A0A] uppercase tracking-wide">Internal (admin only)</h3>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 flex gap-2">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>The Internal Estimated Value and notes are for accounting only. They are <b>never printed on the donor receipt</b> — the donor is responsible for determining fair market value.</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#4B5563] font-medium">Internal Estimated Value (USD)</label>
                <input
                  type="number" step="0.01" min="0"
                  value={form.internal_estimated_value}
                  onChange={(e) => setForm({ ...form, internal_estimated_value: e.target.value })}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  data-testid="in-kind-iev"
                />
              </div>
              <div>
                <label className="text-xs text-[#4B5563] font-medium">Internal Notes</label>
                <input
                  type="text"
                  value={form.internal_notes}
                  onChange={(e) => setForm({ ...form, internal_notes: e.target.value })}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  data-testid="in-kind-notes"
                />
              </div>
            </div>
          </section>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800" data-testid="in-kind-error">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl sticky bottom-0">
          <button
            onClick={onClose}
            className="text-sm text-[#4B5563] font-medium hover:text-[#0A0A0A]"
            data-testid="in-kind-cancel"
            disabled={saving}
          >Cancel</button>
          <div className="flex items-center gap-2">
            <button
              onClick={() => submit({ andSend: false })}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-[#0A0A0A] rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
              data-testid="in-kind-save"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save
            </button>
            <button
              onClick={() => submit({ andSend: true })}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-[#BA1826] text-white rounded-lg text-sm font-semibold hover:bg-[#a01523] disabled:opacity-50"
              data-testid="in-kind-save-send"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Save &amp; Send Receipt
            </button>
          </div>
        </div>
      </div>

      <MediaPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        mode="multi"
        excludeUrls={(form.photo_ids || []).map((id) => mediaServeUrl(id, 'desktop'))}
        onPick={(_url, m) => addPhotoIds([m.id])}
      />
    </div>
  );
}
